import { inject, Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { ApiResponseModel, PaginatedListModel } from '../shared/models/result.model';
import {
  AppointmentCheck,
  AppointmentCheckQuery,
  ActivityDetails,
  CheckInUpdatePayload,
  AppointmentDetail,
  HospitalisationType,
  AppointmentTaskUpdatePayload,
  CheckInCreatePayload
} from '../shared/models/appointment-check.model';

@Injectable({
  providedIn: 'root'
})
export class AppointmentCheckService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = environment.appointmentBaseUrl;
  private readonly serviceUrl = `${this.baseUrl}/AppointmentCheck`;

  getAppointmentChecks(query: AppointmentCheckQuery): Observable<PaginatedListModel<AppointmentCheck>> {
    const { companyId, startDate, endDate, patientId, pageNumber, pageSize, nextPageToken } = query;
    const params: any = {
      companyId,
      startDate,
      endDate,
      patientId,
      pageNumber,
      pageSize
    };
    if (nextPageToken) {
      params.nextPageToken = nextPageToken;
    }
    return this.http.get<PaginatedListModel<AppointmentCheck>>(`${this.serviceUrl}/v1/GetDetailedActivity`, { params });
  }

  getActivityByAppointmentIdAndDate(
    appointmentId: string,
    date: string
  ): Observable<ApiResponseModel<ActivityDetails>> {
    return this.http.get<ApiResponseModel<ActivityDetails>>(
      `${this.baseUrl}/Appointment/GetActivityByAppointmentIdAndDate`,
      {
        params: { appointmentId, date }
      }
    );
  }

  updateCheckInTime(
    appointmentId: string,
    date: string,
    originalCheckInTime: string | null,
    newCheckInTime: string
  ): Observable<ApiResponseModel<any>> {
    const payload = {
      originalCheckedInTime: originalCheckInTime,
      newCheckedInTime: newCheckInTime
    };
    return this.http.put<ApiResponseModel<any>>(`${this.serviceUrl}/${appointmentId}/checkin`, payload);
  }

  updateCheckOutTime(
    appointmentId: string,
    date: string,
    originalCheckOutTime: string | null,
    newCheckOutTime: string
  ): Observable<ApiResponseModel<any>> {
    const payload = {
      originalCheckedOutTime: originalCheckOutTime,
      newCheckedOutTime: newCheckOutTime
    };
    return this.http.put<ApiResponseModel<any>>(`${this.serviceUrl}/${appointmentId}/checkout`, payload);
  }

  updateCheckIn(payload: CheckInUpdatePayload): Observable<ApiResponseModel<any>> {
    return this.http.put<ApiResponseModel<any>>(`${this.baseUrl}/Appointment/v2/CheckIn`, payload);
  }

  createCheckIn(payload: CheckInCreatePayload): Observable<ApiResponseModel<any>> {
    return this.http.post<ApiResponseModel<any>>(`${this.baseUrl}/Appointment/v2/CheckIn`, payload);
  }

  updateCheckOut(payload: {
    appointmentId: string;
    previousDateTime: string;
    updatedDateTime: string;
  }): Observable<ApiResponseModel<any>> {
    const apiUrl = `${this.baseUrl}/Appointment/CheckOut`;
    return this.http.put<ApiResponseModel<any>>(apiUrl, payload);
  }

  checkOutForce(payload: CheckInCreatePayload): Observable<ApiResponseModel<any>> {
    return this.http.post<ApiResponseModel<any>>(`${this.baseUrl}/Appointment/v2/CheckOutForce`, payload);
  }

  checkInForce(payload: CheckInCreatePayload): Observable<ApiResponseModel<any>> {
    return this.http.post<ApiResponseModel<any>>(`${this.baseUrl}/Appointment/v2/CheckInForce`, payload);
  }

  getAppointmentDetail(appointmentId: string, date: string): Observable<ApiResponseModel<AppointmentDetail>> {
    return this.http.get<ApiResponseModel<AppointmentDetail>>(
      `${this.baseUrl}/Appointment/v2/detail/${appointmentId}`,
      { params: { Date: date } }
    );
  }

  updateAppointmentTask(payload: AppointmentTaskUpdatePayload): Observable<ApiResponseModel<any>> {
    return this.http.post<ApiResponseModel<any>>(`${this.baseUrl}/Appointment/AppointmentTaskUpdate`, payload);
  }

  getHospitalisationTypes(): Observable<ApiResponseModel<HospitalisationType[]>> {
    return this.http.get<ApiResponseModel<HospitalisationType[]>>(
      `${this.baseUrl}/Appointment/GetHospitalisationTypes`
    );
  }
}
